import { useToast } from '@chakra-ui/react';
import { AxiosResponse } from 'axios';
import { AuthDomain } from 'domains/AuthDomain';
import {
    AuthRequestChangePassword,
    AuthRequestDTO,
    AuthRequestForgotPassword,
    AuthRequestResetPassword
} from 'models';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Auth from 'services/auth';
import { AuthContextData, AuthContextProps, AuthUser } from './interface';

// 1. IMPORTAÇÕES NECESSÁRIAS PARA O WEBSOCKET
import { environments } from 'environments/environments';
import jwt_decode from 'jwt-decode';
import { AuthTokens } from 'models';
import { WebSocketContext } from '../websocket'; // Importa o contexto simplificado

export const AuthContext = createContext({} as AuthContextData);

export const useAuthContext = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }: AuthContextProps) => {
  const toast = useToast();
  const [isBackoffice, setIsBackoffice] = useState<boolean>(false);
  const [pathnameCurrent, setPathnameCurrent] = useState('');
  const { pathname } = useLocation();
  const [currentUser, setCurrentUser] = useState<AuthUser | undefined>(() => {
    const usr = [
      'accessToken', 'expiresIn', 'tokenType', 'refreshToken', 'idToken'
    ]
      .map(token => ({ [token]: sessionStorage.getItem(token) || '' }))
      .reduce(
        (previousValue, currentValue) =>
          Object.assign(previousValue, currentValue),
        {}
      ) as unknown as AuthUser;
    return usr.idToken ? usr : undefined;
  });
  const [permissionArray, setPermissionArray] = useState<string[]>(['']);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [loadingAuth, setLoadingAuth] = useState(true);

  // 2. ADICIONE O ESTADO E A REFERÊNCIA DO WEBSOCKET AQUI
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting' | 'error'>('disconnected');
  const webSocketRef = useRef<WebSocket | null>(null);

  // 3. FUNÇÃO PARA DESCONECTAR O WEBSOCKET
  const disconnectWebSocket = useCallback(() => {
    if (webSocketRef.current) {
        console.log('[WebSocket AuthProvider] Desconectando...');
        webSocketRef.current.close();
        webSocketRef.current = null;
        setConnectionStatus('disconnected');
    }
  }, []);

  const signup = ({ nomeUsuario, senha }: AuthRequestDTO): Promise<void> => {
    return Auth.signUp({ nomeUsuario, senha });
  };

  const validateCaptcha = async (token: string) => {
    return Auth.validateCaptcha(token);
  };

  const getPermissions = async (user: AuthUser) => {
    const authDomain = new AuthDomain();
    const { bk, mountPermissions } = authDomain.getPermissions(user);

    setPermissionArray(mountPermissions);
    setIsBackoffice(bk);

    if (mountPermissions.includes('integracao:contratos')) {
        const toastDuration = 3000;
        toast({
            title: 'Login negado',
            description: "Usuario de integração não possui permissão para acesso a Plataforma",
            status: 'error',
            duration: toastDuration,
            isClosable: true, 
        })
        setLoadingAuth(false);
        setTimeout(() => {
            signout();
        }, toastDuration - 500);
        return false; // Retorna false para indicar falha na permissão
    }


    return true; // Retorna true para indicar sucesso
  };
  
  const signout = async (): Promise<void> => {
    const authDomain = new AuthDomain();
    authDomain.signOutUser();
    // *** DESCONECTA O WEBSOCKET NO LOGOUT ***
    disconnectWebSocket(); 
  };
  
  const reset = (data: AuthRequestResetPassword): Promise<AxiosResponse<void>> => {
    return Auth.resetPassword(data);
  };

  const change = (data: AuthRequestChangePassword): Promise<AxiosResponse<void>> => {
    return Auth.changePassword(data);
  };

  const forgot = (data: AuthRequestForgotPassword): Promise<AxiosResponse<void>> => {
    return Auth.forgotPassword(data);
  };

  // 4. LÓGICA DE CONEXÃO MOVIDA PARA O USEEFFECT PRINCIPAL
  useEffect(() => {
    setLoadingAuth(true);

    // Se não há usuário, desconecta e para
    if (!currentUser?.idToken) {
        setAuthenticated(false);
        disconnectWebSocket();
        setLoadingAuth(false);
        return;
    }
    
    Auth.state(currentUser)
      .then(async response => {
        if (response) { // Se Auth.state confirma que o usuário é válido
          setAuthenticated(true);
          const permissionsOk = await getPermissions(currentUser);
          
          if (!permissionsOk) return; // Não conecta se as permissões não estiverem OK

          // *** É AQUI QUE A MÁGICA ACONTECE ***
          // Se está autenticado e tem permissão, conecta o WebSocket
          if (webSocketRef.current) return; // Já conectado, não faz nada
          
          try {
            const decodedIdToken = jwt_decode(currentUser.idToken) as AuthTokens;
            const guidUsuario = decodedIdToken?.['custom:usuario_guid'];
            const nomeUsuario = decodedIdToken?.preferred_username;

            if (guidUsuario && nomeUsuario) {
                const wsUrl = new URL(environments.WEBSOCKET_CONNECTIONS);
                wsUrl.searchParams.append('guidUsuario', guidUsuario);
                wsUrl.searchParams.append('nomeUsuario', nomeUsuario);

                console.log(`[WebSocket AuthProvider] Conectando a: ${wsUrl.href}`);
                setConnectionStatus('connecting');

                const ws = new WebSocket(wsUrl.href);
                webSocketRef.current = ws;

                ws.onopen = () => {
                    console.log('[WebSocket AuthProvider] Conexão estabelecida.');
                    setConnectionStatus('connected');
                };
                ws.onclose = () => {
                    console.log('[WebSocket AuthProvider] Conexão fechada.');
                    setConnectionStatus('disconnected');
                    webSocketRef.current = null;
                };
                ws.onerror = (error) => {
                    console.error('[WebSocket AuthProvider] Erro:', error);
                    setConnectionStatus('error');
                };
            }
          } catch (error) {
              console.error('[WebSocket AuthProvider] Falha ao iniciar conexão:', error);
              setConnectionStatus('error');
          }

        } else {
            setAuthenticated(false);
            disconnectWebSocket(); // Desconecta se a validação falhar
        }
      })
      .finally(() => setLoadingAuth(false));

  }, [currentUser]); // A dependência está correta

  useEffect(() => {
    setPathnameCurrent(pathname);
  }, [pathname]);

  const sendMessage = useCallback((message: object) => {
    if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
        webSocketRef.current.send(JSON.stringify(message));
    } else {
        console.error('[WebSocket] Não foi possível enviar a mensagem. Conexão não está aberta.');
    }
  }, []);

  const value = {
    pathnameCurrent, isBackoffice, currentUser, permissionArray,
    authenticated, loadingAuth, signup, change, signout,
    reset, forgot, validateCaptcha, setCurrentUser, setLoadingAuth
  };
  
  // 5. ENVOLVA O `CHILDREN` COM OS DOIS PROVIDERS
  return (
      <AuthContext.Provider value={value}>
          <WebSocketContext.Provider value={{ connectionStatus, sendMessage }}>
              {children}
          </WebSocketContext.Provider>
      </AuthContext.Provider>
  );
};